import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sportingService, type Country } from '../../services/sportingService';
import { Search, Globe, Users, MapPin, TrendingUp, Filter } from 'lucide-react';
import toast from 'react-hot-toast';

// Mapeo de países a códigos ISO para las banderas
const countryToCode: Record<string, string> = {
  'Spain': 'es', 'England': 'gb-eng', 'France': 'fr', 'Germany': 'de', 'Italy': 'it',
  'Portugal': 'pt', 'Brazil': 'br', 'Argentina': 'ar', 'Netherlands': 'nl', 'Belgium': 'be',
  'Croatia': 'hr', 'Uruguay': 'uy', 'Colombia': 'co', 'Mexico': 'mx', 'Chile': 'cl',
  'Poland': 'pl', 'Austria': 'at', 'Switzerland': 'ch', 'Denmark': 'dk', 'Sweden': 'se',
  'Norway': 'no', 'Czech Republic': 'cz', 'Serbia': 'rs', 'Turkey': 'tr', 'Greece': 'gr',
  'Japan': 'jp', 'South Korea': 'kr', 'Australia': 'au', 'United States': 'us', 'Canada': 'ca',
  'Nigeria': 'ng', 'Senegal': 'sn', 'Ghana': 'gh', 'Cameroon': 'cm', 'Morocco': 'ma',
  'Egypt': 'eg', 'Algeria': 'dz', 'Tunisia': 'tn', 'Ivory Coast': 'ci', 'South Africa': 'za',
  'Scotland': 'gb-sct', 'Wales': 'gb-wls', 'Northern Ireland': 'gb-nir', 'Republic of Ireland': 'ie',
  'Russia': 'ru', 'Ukraine': 'ua', 'Romania': 'ro', 'Hungary': 'hu', 'Slovakia': 'sk',
  'Bosnia-Herzegovina': 'ba', 'Slovenia': 'si', 'Albania': 'al', 'North Macedonia': 'mk',
  'Ecuador': 'ec', 'Paraguay': 'py', 'Peru': 'pe', 'Venezuela': 've', 'Bolivia': 'bo',
  'Costa Rica': 'cr', 'Panama': 'pa', 'Jamaica': 'jm', 'Honduras': 'hn', 'El Salvador': 'sv',
  'China PR': 'cn', 'Iran': 'ir', 'Saudi Arabia': 'sa', 'Qatar': 'qa', 'United Arab Emirates': 'ae',
  'Israel': 'il', 'India': 'in', 'Thailand': 'th', 'Vietnam': 'vn', 'Indonesia': 'id',
};

// Mapeo de continentes
const continentMap: Record<string, string[]> = {
  'Europe': [
    'Spain', 'England', 'France', 'Germany', 'Italy', 'Portugal', 'Netherlands', 'Belgium',
    'Croatia', 'Poland', 'Austria', 'Switzerland', 'Denmark', 'Sweden', 'Norway',
    'Czech Republic', 'Serbia', 'Turkey', 'Greece', 'Scotland', 'Wales', 'Northern Ireland',
    'Republic of Ireland', 'Russia', 'Ukraine', 'Romania', 'Hungary', 'Slovakia',
    'Bosnia-Herzegovina', 'Slovenia', 'Albania', 'North Macedonia', 'Finland', 'Iceland',
    'Bulgaria', 'Lithuania', 'Latvia', 'Estonia', 'Luxembourg', 'Montenegro', 'Kosovo',
    'Cyprus', 'Malta', 'Belarus', 'Moldova', 'Georgia', 'Armenia', 'Azerbaijan',
  ],
  'South America': [
    'Brazil', 'Argentina', 'Uruguay', 'Colombia', 'Chile', 'Ecuador', 'Paraguay', 'Peru',
    'Venezuela', 'Bolivia',
  ],
  'North America': [
    'United States', 'Canada', 'Mexico', 'Costa Rica', 'Panama', 'Jamaica', 'Honduras',
    'El Salvador', 'Guatemala', 'Nicaragua', 'Trinidad and Tobago', 'Cuba', 'Haiti',
  ],
  'Africa': [
    'Nigeria', 'Senegal', 'Ghana', 'Cameroon', 'Morocco', 'Egypt', 'Algeria', 'Tunisia',
    'Ivory Coast', 'South Africa', 'Mali', 'Burkina Faso', 'Congo', 'Guinea', 'Kenya',
    'Zimbabwe', 'Uganda', 'Zambia', 'Angola', 'Mozambique', 'Ethiopia', 'Tanzania',
  ],
  'Asia': [
    'Japan', 'South Korea', 'China PR', 'Iran', 'Saudi Arabia', 'Qatar', 'United Arab Emirates',
    'Israel', 'India', 'Thailand', 'Vietnam', 'Indonesia', 'Uzbekistan', 'Syria', 'Iraq',
    'Lebanon', 'Jordan', 'Palestine', 'Oman', 'Kuwait', 'Bahrain', 'Yemen',
  ],
  'Oceania': [
    'Australia', 'New Zealand', 'Fiji', 'Papua New Guinea', 'Solomon Islands',
  ],
};

const CountryExplorerPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContinent, setSelectedContinent] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'players'>('players');

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const data = await sportingService.getCountries();
      setCountries(data.countries);
    } catch (error: any) {
      toast.error(error.message || 'Error al cargar países');
      console.error('Error loading countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCountryCode = (countryName: string): string => {
    return countryToCode[countryName] || countryName.toLowerCase().substring(0, 2);
  };

  const getContinent = (countryName: string): string => {
    for (const [continent, countries] of Object.entries(continentMap)) {
      if (countries.includes(countryName)) {
        return continent;
      }
    }
    return 'Other';
  };

  const filteredCountries = useMemo(() => {
    let filtered = countries;

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.country.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por continente
    if (selectedContinent !== 'all') {
      filtered = filtered.filter(c => getContinent(c.country) === selectedContinent);
    }

    // Ordenar
    if (sortBy === 'name') {
      filtered = [...filtered].sort((a, b) => a.country.localeCompare(b.country));
    } else {
      filtered = [...filtered].sort((a, b) => b.player_count - a.player_count);
    }

    return filtered;
  }, [countries, searchTerm, selectedContinent, sortBy]);

  const continentStats = useMemo(() => {
    const stats: Record<string, number> = {};
    countries.forEach(country => {
      const continent = getContinent(country.country);
      stats[continent] = (stats[continent] || 0) + country.player_count;
    });
    return stats;
  }, [countries]);

  const totalPlayers = countries.reduce((sum, c) => sum + c.player_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('countries.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="relative p-10 text-center space-y-6 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 text-white shadow-xl rounded-2xl border border-blue-500/20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-purple-800/90 rounded-2xl"></div>
        <div className="relative z-10">
          <Globe className="w-16 h-16 mx-auto mb-4 text-blue-100" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('countries.title')}
          </h1>
          <p className="text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
            {t('countries.subtitle')}
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium mb-1">{t('countries.totalCountries')}</p>
              <p className="text-3xl font-bold">{countries.length}</p>
            </div>
            <MapPin className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium mb-1">{t('countries.totalPlayers')}</p>
              <p className="text-3xl font-bold">{totalPlayers.toLocaleString()}</p>
            </div>
            <Users className="w-12 h-12 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm font-medium mb-1">{t('countries.averagePerCountry')}</p>
              <p className="text-3xl font-bold">{Math.round(totalPlayers / countries.length)}</p>
            </div>
            <TrendingUp className="w-12 h-12 text-indigo-200" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('countries.searchCountry')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Continent Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedContinent}
              onChange={(e) => setSelectedContinent(e.target.value)}
              className="pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer bg-white min-w-[200px]"
            >
              <option value="all">{t('countries.allContinents')}</option>
              {Object.keys(continentMap).map(continent => (
                <option key={continent} value={continent}>
                  {continent} ({continentStats[continent] || 0})
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'players')}
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer bg-white"
          >
            <option value="players">{t('countries.mostPlayers')}</option>
            <option value="name">{t('countries.alphabetical')}</option>
          </select>
        </div>
      </div>

      {/* Countries Grid */}
      {filteredCountries.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <Globe className="w-20 h-20 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('countries.noCountriesFound')}</h3>
          <p className="text-gray-600">{t('countries.tryDifferentFilters')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredCountries.map((country) => {
            const countryCode = getCountryCode(country.country);
            const continent = getContinent(country.country);
            
            return (
              <button
                key={country.country}
                onClick={() => navigate(`/sporting/countries/${encodeURIComponent(country.country)}`)}
                className="group relative bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-200 hover:border-blue-300 p-6 transition-all duration-300 hover:-translate-y-1"
              >
                {/* Flag */}
                <div className="relative mb-4">
                  <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 shadow-md">
                  <img
                    src={`https://flagcdn.com/w320/${countryCode}.png`}
                    alt={country.country}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      // Asegura que el texto del país sea un string
                      const countryName = typeof country === 'string'
                        ? country
                        : (country as any)?.name || 'Unknown';
                      
                      img.src = `https://placehold.co/320x240/e5e7eb/6b7280?text=${encodeURIComponent(countryName)}`;
                    }}
                    
                  />

                  </div>
                  
                  {/* Badge con cantidad de jugadores */}
                  <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    {country.player_count}
                  </div>
                </div>

                {/* Country Name */}
                <h3 className="font-bold text-gray-900 mb-1 text-center group-hover:text-blue-600 transition-colors line-clamp-2">
                  {country.country}
                </h3>
                
                {/* Continent */}
                <p className="text-xs text-gray-500 text-center">{continent}</p>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl pointer-events-none" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CountryExplorerPage;
